<template>
  <div class="is-inline-block" v-if="hasTransferAccess && isTransferable">
    <div class="quick-action" v-if="!isTransferDialogActive">
      <button class="button" @click="onOpenTransfer()" title="Transfer inventory to another dealership" v-tooltip="'Inter-Dealership Transfer'">
        <b-icon pack="fal" icon="truck-loading" custom-size="fa-lg"></b-icon>
      </button>
    </div>
    <portal to="global-modal-portal" v-else-if="blockingTransfer">
      <b-modal :active.sync="isTransferDialogActive" :width="560" scroll="keep" :has-modal-card="true">
        <div class="modal-card transfer-modal">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <i class="fal fa-truck-loading fa-sm"></i> Already Transferred
            </p>
          </header>
          <section class="modal-card-body">
            <div class="notification is-warning is-light is-size-7">
              <p>
                <strong>{{ currentInventory.stockNo }}</strong> has already been transferred and cannot be
                transferred again.
              </p>
            </div>
            <div class="transfer-vehicle-meta">
              <div class="transfer-meta-row">
                <span class="transfer-meta-label">Transfer</span>
                <span class="transfer-meta-value">#{{ blockingTransfer.id }} &mdash; {{ blockingTransfer.status }}</span>
              </div>
              <div class="transfer-meta-row">
                <span class="transfer-meta-label">Destination</span>
                <span class="transfer-meta-value">{{ blockingTransfer.targetDealerName }}</span>
              </div>
              <div class="transfer-meta-row" v-if="blockingTransfer.targetInventoryStockNo">
                <span class="transfer-meta-label">Destination Stock</span>
                <span class="transfer-meta-value">{{ blockingTransfer.targetInventoryStockNo }}</span>
              </div>
              <div class="transfer-meta-row">
                <span class="transfer-meta-label">Initiated</span>
                <span class="transfer-meta-value">{{ blockingTransfer.initiatedAt | formatDate }}</span>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button class="button" @click="onCancelTransfer()">Close</button>
          </footer>
        </div>
      </b-modal>
    </portal>
    <portal to="global-modal-portal" v-else>
      <b-modal :active.sync="isTransferDialogActive" :width="600" scroll="keep" :has-modal-card="true">
        <div class="modal-card transfer-modal">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <i class="fal fa-truck-loading fa-sm"></i> Transfer to Dealership
            </p>
          </header>
          <section class="modal-card-body">

            <div class="transfer-error-block" v-if="serverErrors.length > 0 || serverErrorMessage.length > 0">
              <div class="notification is-danger is-light is-size-7">
                <p v-if="serverErrors.length > 0" v-for="error in serverErrors" :key="error.errorMessage">
                  <i class="fas fa-exclamation-circle"></i> {{ error.errorMessage }}
                </p>
                <p v-if="serverErrorMessage.length > 0">
                  <i class="fas fa-exclamation-circle"></i> {{ serverErrorMessage }}
                </p>
              </div>
            </div>

            <div class="transfer-vehicle-summary">
              <div class="transfer-vehicle-header">
                <div>
                  <span class="transfer-vehicle-name">{{ currentInventory.name }}</span>
                  <span class="tag is-small transfer-type-tag" :class="stockNoTagClass">{{ currentInventory.type }}</span>
                </div>
                <span class="tag is-medium transfer-stock-tag" :class="stockNoTagClass">{{ currentInventory.stockNo }}</span>
              </div>
              <div class="transfer-vehicle-meta">
                <div class="transfer-meta-row">
                  <span class="transfer-meta-label">VIN</span>
                  <span class="transfer-meta-value">{{ currentInventory.vinNo || '-' }}</span>
                </div>
                <div class="transfer-meta-row">
                  <span class="transfer-meta-label">Mileage</span>
                  <span class="transfer-meta-value">{{ currentInventory.usage ? formatMileage(currentInventory.usage) + ' miles' : '-' }}</span>
                </div>
                <div class="transfer-meta-row" v-if="exteriorColor">
                  <span class="transfer-meta-label">Color</span>
                  <span class="transfer-meta-value">{{ exteriorColor }}</span>
                </div>
              </div>
              <div class="columns is-mobile is-gapless transfer-cost-row">
                <div class="column is-4">
                  <span class="transfer-label">Purchase Cost</span>
                  <span class="transfer-cost">{{ currentInventory.purchaseCost | currency }}</span>
                </div>
                <div class="column is-4">
                  <span class="transfer-label">Recon Cost</span>
                  <span class="transfer-cost">{{ currentInventory.reconCost | currency }}</span>
                </div>
                <div class="column is-4">
                  <span class="transfer-label">Total Cost</span>
                  <span class="transfer-cost transfer-cost-total">{{ currentInventory.purchaseCostTotal | currency }}</span>
                </div>
              </div>
            </div>

            <div class="transfer-form">
              <div class="field">
                <label class="label is-small">Target Dealership <b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                <div class="control">
                  <multiselect
                    :showLabels="false"
                    name="targetDealership"
                    data-vv-as="target dealership"
                    v-model="selectedSupplier"
                    :options="eligibleSuppliers"
                    :custom-label="supplierLabel"
                    trackBy="supplierId"
                    placeholder="Select a dealership..."
                    :loading="isLoadingSuppliers"
                    :allowEmpty="false"
                    :hideSelected="true"
                    :searchable="true">
                    <template slot="noResult">No matching dealerships</template>
                    <template slot="noOptions">No eligible dealerships found. Link a supplier to a dealer first.</template>
                  </multiselect>
                </div>
              </div>

              <div class="field">
                <user-selector
                  :selectedDealer="destinationDealer"
                  v-model="selectedPurchasingAgent"
                  label="Purchasing Agent"
                  validateAs="purchasing agent"
                  :required="true"
                  :disabled="!selectedSupplier"></user-selector>
                <p class="help" v-if="!selectedSupplier">Choose the target dealership first.</p>
              </div>

              <div class="transfer-price-row">
                <div class="transfer-price-col transfer-price-col-code">
                  <label class="label is-small">Deal Code</label>
                  <div class="control has-icons-left">
                    <input class="input" type="text" maxlength="10" placeholder="Optional" v-model="dealCode" />
                    <span class="icon is-small is-left">
                      <i class="fal fa-hashtag"></i>
                    </span>
                  </div>
                </div>
                <div class="transfer-price-col transfer-price-col-price">
                  <label class="label is-small">Wholesale Sale Price <b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                  <div class="control has-icons-left">
                    <money name="transferSalePrice" class="input" type="text" placeholder="0.00" v-model="salePrice" v-bind="$globalMoneyFormat"></money>
                    <span class="icon is-small is-left">
                      <i class="fas fa-dollar-sign"></i>
                    </span>
                  </div>
                  <div class="transfer-profit-indicator" v-if="salePrice > 0 && totalCost > 0">
                    <span :class="profitClass">
                      <i :class="profitIcon"></i>
                      {{ profitLabel }} {{ Math.abs(profitAmount) | currency }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="transfer-confirm-block" v-if="selectedSupplier && salePrice > 0">
              <div class="columns is-mobile is-gapless is-vcentered">
                <div class="column is-narrow" style="padding-right: 0.75rem;">
                  <span class="icon has-text-info"><i class="fal fa-info-circle fa-lg"></i></span>
                </div>
                <div class="column">
                  <p class="is-size-7">
                    A wholesale deal will be created to <strong>{{ selectedSupplier.dealerName }}</strong> for <strong>{{ salePrice | currency }}</strong>.
                    {{ transferOutcomeNote }}
                  </p>
                </div>
              </div>
            </div>

          </section>
          <footer class="modal-card-foot">
            <button class="button is-success" :class="{'is-loading': isSaving}" @click="onConfirmTransfer()" :disabled="!isFormValid || isSaving">
              <span class="icon"><i class="fal fa-check"></i></span>
              <span>Confirm Transfer</span>
            </button>
            <button class="button" @click="onCancelTransfer()" :disabled="isSaving">Cancel</button>
          </footer>
        </div>
      </b-modal>
    </portal>
  </div>
</template>

<script>
import eventBus from '@/eventBus'
import inventoryService from '../../services/inventoryService'
import UserSelector from '@/components/generic/UserSelector'
import { createNamespacedHelpers } from 'vuex'
const mapUserGetters = createNamespacedHelpers('user').mapGetters

export default {
  name: 'InventoryTransferAction',
  components: {
    'user-selector': UserSelector
  },
  props: {
    currentInventory: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isTransferDialogActive: false,
      isLoadingSuppliers: false,
      isSaving: false,
      eligibleSuppliers: [],
      selectedSupplier: null,
      selectedPurchasingAgent: null,
      blockingTransfer: null,
      dealCode: '',
      salePrice: 0,
      serverErrors: [],
      serverErrorMessage: ''
    }
  },
  computed: {
    ...mapUserGetters(['hasFeatureAccess']),
    hasTransferAccess () {
      return this.hasFeatureAccess('inventory.transfer')
    },
    isTransferable () {
      return this.currentInventory && !this.currentInventory.sold && this.currentInventory.status !== 'Archived' && this.currentInventory.status !== 'Cancelled'
    },
    transferOutcomeNote () {
      if (this.currentInventory && this.currentInventory.dealerCdkEnabled) {
        return 'The deal will be created as Delivered, the vehicle marked as sold and queued for CDK as Gone. It is loaded in the receiving dealership once that CDK sync completes.'
      }
      return 'The deal will be created as Delivered and the vehicle marked as sold. It is loaded in the receiving dealership shortly afterwards.'
    },
    isFormValid () {
      return this.selectedSupplier && this.salePrice > 0 && this.selectedPurchasingAgent
    },
    stockNoTagClass () {
      switch (this.currentInventory.type) {
        case 'New': return 'is-link'
        case 'Purchased': return 'is-info'
        case 'Consigned': return 'is-warning'
        case 'TradeIn': return 'is-success'
        case 'LeaseReturn': return 'is-dark'
        case 'ForPpaAppraisal': return 'is-primary'
        case 'PpaClosed': return 'is-light'
        default: return 'is-dark'
      }
    },
    exteriorColor () {
      if (!this.currentInventory || !this.currentInventory.attributes) return ''
      var color = this.currentInventory.attributes.find(x => x.name === 'ExteriorColor')
      return color ? color.value : ''
    },
    totalCost () {
      return this.currentInventory.purchaseCostTotal || 0
    },
    profitAmount () {
      return this.salePrice - this.totalCost
    },
    profitClass () {
      if (this.profitAmount > 0) return 'has-text-success'
      if (this.profitAmount < 0) return 'has-text-danger'
      return 'has-text-grey'
    },
    profitIcon () {
      if (this.profitAmount > 0) return 'fas fa-arrow-up fa-sm'
      if (this.profitAmount < 0) return 'fas fa-arrow-down fa-sm'
      return 'fas fa-minus fa-sm'
    },
    profitLabel () {
      if (this.profitAmount > 0) return 'Profit'
      if (this.profitAmount < 0) return 'Loss'
      return 'Break even'
    },
    destinationDealer () {
      // UserSelector filters allUsers by selectedDealer.id, and allUsers is subscriber-wide, so the receiving
      // dealer's people are already loaded - no extra fetch needed.
      return this.selectedSupplier
        ? { id: this.selectedSupplier.dealerId, name: this.selectedSupplier.dealerName }
        : null
    }
  },
  watch: {
    selectedSupplier () {
      // The agent belongs to the receiving dealer, so changing dealership invalidates the choice.
      this.selectedPurchasingAgent = null
    }
  },
  methods: {
    supplierLabel ({ supplierName, dealerName }) {
      return `${supplierName} (${dealerName})`
    },
    formatMileage (value) {
      return value ? new Intl.NumberFormat().format(value) : '0'
    },
    onOpenTransfer () {
      this.serverErrors = []
      this.serverErrorMessage = ''
      this.selectedSupplier = null
      this.selectedPurchasingAgent = null
      this.blockingTransfer = null
      this.dealCode = ''
      this.salePrice = this.currentInventory.askingPrice || 0
      this.isTransferDialogActive = true

      // Asked for on open rather than on inventory load, so the list and view screens pay nothing for it.
      // A null answer means the vehicle is free; the server refuses a duplicate either way.
      inventoryService.getBlockingTransfer(this.currentInventory.id).then(existing => {
        this.blockingTransfer = existing || null
        if (!this.blockingTransfer) this.loadEligibleSuppliers()
      }).catch(() => {
        // Never trap the user in an empty dialog because this lookup failed - the server guard still holds.
        this.blockingTransfer = null
        this.loadEligibleSuppliers()
      })
    },
    onCancelTransfer () {
      this.isTransferDialogActive = false
    },
    loadEligibleSuppliers () {
      this.isLoadingSuppliers = true
      inventoryService.getTransferEligibleSuppliers(this.currentInventory.id).then(response => {
        this.eligibleSuppliers = response
        this.isLoadingSuppliers = false
      }).catch(error => {
        this.serverErrorMessage = error.message || 'Failed to load eligible suppliers.'
        this.isLoadingSuppliers = false
      })
    },
    onConfirmTransfer () {
      if (!this.isFormValid) return

      this.isSaving = true
      this.serverErrors = []
      this.serverErrorMessage = ''

      const model = {
        inventoryId: this.currentInventory.id,
        targetSupplierId: this.selectedSupplier.supplierId,
        salePrice: this.salePrice,
        dealCode: this.dealCode || '',
        purchasingAgentUserId: this.selectedPurchasingAgent.id
      }

      inventoryService.transferInventory(model).then(response => {
        this.isSaving = false
        this.isTransferDialogActive = false
        this.$buefy.toast.open({
          message: `Transfer initiated to ${this.selectedSupplier.dealerName}. Deal: ${response.dealCode}`,
          type: 'is-success',
          duration: 5000
        })
        eventBus.$emit('inventory-changed', [this.currentInventory])
      }).catch(error => {
        this.isSaving = false
        if (error && error.message) {
          this.serverErrorMessage = error.message
        } else if (error && error.Message) {
          this.serverErrorMessage = error.Message
        } else {
          this.serverErrorMessage = 'An unexpected error occurred during the transfer.'
        }
      })
    }
  }
}
</script>

<style scoped>
  .transfer-modal {
    width: 600px !important;
    min-width: 600px;
    max-width: 600px;
  }

  .transfer-modal .modal-card-head {
    padding: 16px 24px;
  }

  .transfer-modal .modal-card-title {
    font-size: 0.95rem;
    font-weight: 600;
  }

  .transfer-modal .modal-card-title i {
    margin-right: 6px;
    opacity: 0.6;
  }

  .transfer-modal .modal-card-body {
    padding: 24px;
    min-height: 380px;
  }

  .transfer-modal .modal-card-foot {
    padding: 16px 24px;
  }

  .transfer-vehicle-summary {
    background: #f8f9fa;
    border: 1px solid #eee;
    border-radius: 4px;
    padding: 14px 16px;
    margin-bottom: 22px;
  }

  .transfer-vehicle-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .transfer-vehicle-name {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    color: #222;
    margin-bottom: 4px;
  }

  .transfer-type-tag {
    vertical-align: middle;
  }

  .transfer-stock-tag {
    font-weight: 700;
    letter-spacing: 0.03em;
    flex-shrink: 0;
  }

  .transfer-vehicle-meta {
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid #eee;
  }

  .transfer-meta-row {
    display: flex;
    align-items: baseline;
    margin-bottom: 3px;
  }

  .transfer-meta-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #999;
    width: 65px;
    flex-shrink: 0;
  }

  .transfer-meta-value {
    font-size: 0.82rem;
    color: #444;
    font-weight: 500;
  }

  .transfer-label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #888;
    margin-bottom: 2px;
  }

  .transfer-form {
    margin-bottom: 16px;
  }

  .transfer-form .field {
    margin-bottom: 16px;
  }

  .transfer-form .label {
    margin-bottom: 4px;
  }

  .transfer-price-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .transfer-price-col {
    display: flex;
    flex-direction: column;
  }

  .transfer-price-col .label {
    margin-bottom: 4px;
    white-space: nowrap;
    min-height: 22px;
    display: flex;
    align-items: center;
  }

  .transfer-price-col-code {
    flex: 0 0 38%;
  }

  .transfer-price-col-price {
    flex: 1;
  }

  .transfer-confirm-block {
    background: #f0f7ff;
    border-left: 3px solid #3273dc;
    border-radius: 0 4px 4px 0;
    padding: 12px 14px;
  }

  .transfer-confirm-block p {
    line-height: 1.5;
    color: #444;
  }

  .transfer-cost-row {
    border-top: 1px solid #eee;
    padding-top: 10px;
  }

  .transfer-cost {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #555;
  }

  .transfer-cost-total {
    color: #222;
  }

  .transfer-profit-indicator {
    margin-top: 6px;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .transfer-profit-indicator i {
    margin-right: 3px;
  }

  .transfer-error-block {
    margin-bottom: 16px;
  }

  .transfer-error-block .notification {
    margin-bottom: 0;
    padding: 10px 14px;
  }
</style>
